// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The inspector, on the bench (M13-22, 11 §3.1).
//
// The Builder's inspector is `fieldsFor(kind)`: one row per editable member, each a slider or a
// picker bounded to the validator's own range, shown in the unit an author thinks in. The bench
// has the same table and no side panel, so the rows become a **card that floats beside the
// selected part**, inside reach: a slide-rail for every number and every bearing, a pair of
// steppers at its ends, and for a picker the steppers alone, cycling the choices. A rail is
// **pinched and slid** — the value follows the pinch point along the rail, rounded to the field's
// own step —, or **touched and dragged** (a fingertip through the rail, then along it: the 1.0.112
// round, 27 Sep 2026, "it would be a better UI/UX for the user if in build mode the user could drag
// the sliders instead of just the option of hitting the + or - signs"; no author found the pinch,
// and every edit in four device sessions was a stepper), a stepper is **tapped** (a fingertip
// through its face, `tap.ts`), and every target
// is at least `TARGET` across in the *room*, whatever scale the bench is drawn at: the
// recommendation's sixteen millimetres, from the miss-width finding.
//
// **Each slide is one undo gesture.** The Builder keys a slider's drag `${id}:${path}` so a
// hundred change events are one entry; a bench slide is keyed the same way with the slide's own
// number on the end, so two slides of the same rail are two entries an author can take back one
// at a time. A run of taps on a stepper coalesces, as a run of edits to one field does on the
// canvas — five taps of *+* are one thing the author did.
//
// **The card opens on the selection and closes with it.** A placed part is selected; a moved
// part is selected; a tapped callout selects (M13-17); a pinch on empty playfield selects
// nothing (`beginDrag`'s rule). So the card needs no gesture of its own to open or close, and a
// pinch on the card is the card's — the hand is masked from the carry while it is over the card,
// so a rail cannot be mistaken for a handle under it.
//
// **A wire has a card too** (26 Sep 2026: "a wire's capture radius on a card"). A habitrail is a
// layer, chosen by touching its curve (`rampPoints.ts`), and its one number — how near the ball
// must pass to be taken onto it — is a row of the same table (`fieldsFor('wire')`). The card
// floats beside the middle of the wire's control points, and says `kind: 'wire'`, so a part and a
// wire that happen to share an id are two cards.
//
// Pure: fingertips and pinch points in the playfield's frame in, actions out. Nothing here knows
// Three or Unity; `Inspector.cs` is this file, case for case.

import type { Point2, Point3 } from '../../../core/ts/machine'
import type { PlayfieldSize } from '../../builder/viewport'
import {
  fieldsFor,
  readAngle,
  readChoice,
  readNumber,
  shownRange,
  toShown,
  type DeviceField,
  type Editable,
  type FieldKind,
} from '../../builder/fields'
import { TapGate, TOUCH_BEHIND, TOUCH_OUT, type TapState } from './tap'
import type { CarryHand } from './partCarry'
import { BENCH_EN, type BenchKey, type BenchStrings } from './benchStrings'
import { partLabel } from '../../builder/palette'

/** Every target on the card, room metres: the recommendation's sixteen millimetres. */
export const TARGET = 0.016
/** The card, room metres. */
export const CARD_WIDTH = 0.2
export const CARD_PADDING = 0.006
/** One row: a line of text over a rail, with the steppers standing the row's height. */
export const ROW_HEIGHT = 0.026
/**
 * How far above the surface the card floats, room metres: over a part, over the cabinet's side
 * wall where the card hangs past the playfield's edge (the wall stands about four centimetres
 * over the bench's playfield at its usual scale), under a hand.
 */
export const CARD_LIFT = 0.06
/** How far past the playfield's edge the card's near edge sits, room metres. */
export const CARD_BESIDE = 0.05
/**
 * How far the card leans toward the author (the card polish, 26 Sep 2026), radians: its far
 * edge — +y, up the playfield — lifted, so the face turns toward the lockdown bar the author
 * stands at instead of lying flat to the ceiling. Thirty degrees: read from standing without
 * tipping the rows away from a fingertip coming down onto them.
 */
export const CARD_TILT = (30 * Math.PI) / 180

/**
 * Where the card's centre floats, room metres over the surface: `CARD_LIFT` raised by half the
 * card's lean, so its near edge stays at `CARD_LIFT` — over the side wall — however tall the card.
 */
export function cardLift(view: InspectorView): number {
  return CARD_LIFT + (view.height / 2) * Math.sin(CARD_TILT)
}

/**
 * A point in the card's own frame (room metres, origin at its centre, +y up the card, +z off
 * its face) in the playfield's frame at `scale` — `toCard`'s inverse, for a test or a picture
 * to put a hand on a row.
 */
export function cardToPlayfield(view: InspectorView, x: number, y: number, z: number, scale: number): readonly [number, number, number] {
  const s = scale > 0 ? scale : 1
  const c = Math.cos(CARD_TILT)
  const n = Math.sin(CARD_TILT)
  return [view.at[0] + x / s, view.at[1] + (y * c - z * n) / s, (cardLift(view) + y * n + z * c) / s]
}
/** The gap between a stepper and the rail's end. */
const RAIL_GAP = 0.006
/** How far off the card a pinch or a fingertip still counts as on it. */
const CARD_SLACK = 0.008
/** How far above and below the card's plane a hand is "at" it, room metres. */
const CARD_ABOVE = 0.05
const CARD_BELOW = TOUCH_BEHIND
/** A bearing's rail, degrees: the Builder's own range and step. */
const BEARING = { min: -180, max: 180, step: 1 }
/**
 * A bearing's dial (the card polish, 26 Sep 2026), room metres: a target across, so the rim is
 * a fingertip's reach from the centre.
 */
export const DIAL_RADIUS = TARGET / 2
/**
 * Where a dial sits along its row, as a fraction of the rail's length from its left end: in the
 * gap between the label (the left six-tenths) and the value's words (the right end).
 */
const DIAL_ALONG = 0.47
/** How far along the rail a label may run, as a fraction of the rail's length, when no dial stops it. */
const LABEL_SHARE = 0.62
/** A pinch nearer the dial's centre than this points nowhere, room metres, and moves nothing. */
const DIAL_DEAD = 0.002

export interface InspectorRow {
  readonly path: string
  readonly label: string
  /** `rail`: a number or a bearing, slid and stepped. `choice`: stepped through its options. */
  readonly kind: 'rail' | 'choice'
  /** The unit the value is shown in — the field's, or `°` for a bearing, or nothing for a choice. */
  readonly unit: string
  /** The value shown: a number in author units, a bearing in degrees, a choice's index. Undefined for an optional number that is absent. */
  readonly value: number | undefined
  /** The value as words: *76 mm*, *90°*, *Left*, *—*. */
  readonly text: string
  readonly min: number
  readonly max: number
  readonly step: number
  /** A choice's labels, in order; empty for a rail. */
  readonly options: readonly string[]
  /**
   * A bearing, drawn as a dial rather than a straight rail (the card polish, 26 Sep 2026): the
   * knob rides the rim at the bearing — the same direction the part throws on the playfield
   * under it — and a pinch turns it by where it points from the centre.
   */
  readonly dial: boolean
}

export interface InspectorView {
  /** The part's id — or, for a wire's card, the wire's layer id. */
  readonly deviceId: string
  /** The part's kind, or `wire`. */
  readonly kind: FieldKind
  /**
   * What the card is about, over its top edge (the 1.0.113 round, 27 Sep 2026: "the components
   * themselves do not have names or titles"): the part's name and its id — *Right flipper ·
   * flipper2*, *Wire · wireRamp*.
   */
  readonly title: string
  readonly rows: readonly InspectorRow[]
  /** The card's centre, playfield metres. */
  readonly at: Point2
  /** The card's size, room metres. */
  readonly width: number
  readonly height: number
  /** Changes when the card should be repainted: the part, its rows, their values. */
  readonly key: string
}

/** Where a row's parts are, in the card's frame (room metres, origin at the centre, +y up, +x right). */
export interface RowGeometry {
  /** The row's centre line. */
  readonly y: number
  /** The rail's ends, and the knob's centre for the value. */
  readonly railLeft: number
  readonly railRight: number
  readonly railY: number
  readonly knobX: number | undefined
  /** The knob's height on the card: the rail's for a rail, the rim's at the bearing for a dial. */
  readonly knobY: number | undefined
  /** A dial's centre along the row, or nothing for a straight rail; its centre line is `railY`. */
  readonly dialX: number | undefined
  /**
   * Where the label may run, left to right (the card polish, 26 Sep 2026): from just past the −
   * stepper — the steppers stand the row's full height, so a label from the card's edge ran
   * through the − — to six-tenths along the rail, or to the dial's rim.
   */
  readonly labelLeft: number
  readonly labelRight: number
  /** The steppers' centres. */
  readonly minusX: number
  readonly plusX: number
}

/** The bench's word for a field, or an option — the Builder's English where the table has none. */
function wordFor(strings: BenchStrings, key: string, fallback: string): string {
  const known: string | undefined = (strings as Readonly<Record<string, string>>)[key as BenchKey]
  return known ?? fallback
}

function fieldOf(device: Editable, field: DeviceField, strings: BenchStrings): InspectorRow {
  const label = wordFor(strings, `field.${field.path}`, field.label)
  switch (field.type) {
    case 'number': {
      const range = shownRange(field)
      const stored = readNumber(device, field)
      const untilFired = field.path === 'holdMs' && stored !== undefined && stored < 0
      const value = stored === undefined ? undefined : untilFired ? -1 : round(toShown(stored, field.unit), field.step)
      return {
        path: field.path,
        label,
        kind: 'rail',
        unit: field.unit,
        value,
        text: untilFired ? strings['value.untilFired'] : value === undefined ? strings['value.absent'] : `${format(value, field.step)}${field.unit === '' ? '' : ` ${field.unit}`}`,
        min: range.min,
        max: range.max,
        step: field.step,
        options: [],
        dial: false,
      }
    }
    case 'angle': {
      const degrees = round(readAngle(device, field), BEARING.step)
      return {
        path: field.path,
        label,
        kind: 'rail',
        unit: '°',
        value: degrees,
        text: `${format(degrees, 1)}°`,
        min: BEARING.min,
        max: BEARING.max,
        step: BEARING.step,
        options: [],
        dial: true,
      }
    }
    case 'choice': {
      const chosen = readChoice(device, field)
      const index = field.options.findIndex((option) => option.value === chosen)
      const value = index < 0 ? 0 : index
      const options = field.options.map((option) => wordFor(strings, `option.${option.value}`, option.label))
      return {
        path: field.path,
        label,
        kind: 'choice',
        unit: '',
        value,
        text: options[value] ?? strings['value.absent'],
        min: 0,
        max: Math.max(0, field.options.length - 1),
        step: 1,
        options,
        dial: false,
      }
    }
  }
}

/** Rounded to the control's own step, so a rail never shows a value it could not have produced. */
export function round(value: number, step: number): number {
  if (!(step > 0)) return value
  return Number((Math.round(value / step) * step).toFixed(6))
}

function format(value: number, step: number): string {
  const places = step >= 1 ? 0 : Math.min(6, Math.max(0, Math.ceil(-Math.log10(step))))
  return value.toFixed(places)
}

/**
 * The rows of one part: `fieldsFor(kind)`, read off the device, worded in the bench's language
 * (M13-26). None for a kind the table does not know.
 */
export function inspectorRowsOf(device: Editable, strings: BenchStrings = BENCH_EN): readonly InspectorRow[] {
  const fields: readonly DeviceField[] | undefined = fieldsFor(device.kind)
  return (fields ?? []).map((field) => fieldOf(device, field, strings))
}

/** The card's height for a number of rows, room metres. */
export function cardHeight(rows: number): number {
  return CARD_PADDING * 2 + ROW_HEIGHT * Math.max(1, rows)
}

/**
 * The card for a part, or nothing for a part with no rows.
 *
 * **Off the board, on the part's own side** (the 1.0.102 headset session, 26 Sep 2026): past the
 * left edge for a part left of the centre line, past the right edge for one right of it. The card
 * is a hand wide in the room — as wide as the bench's whole playfield at its usual scale — and it
 * used to open over the board beside the part, where it covered the parts next to it: a hand
 * reaching one of them was on the card, its pinch was the card's, and nothing on the board could
 * be picked up while anything was selected. The mini-map takes the other side. Kept level with
 * the part in y, on the playfield where the card is shorter than the playfield. Sizes are room
 * metres and the card is drawn at `1 / scale`, so it is the same card at every bench scale —
 * which is what makes `TARGET` a rule.
 */
export function inspectorViewOf(
  device: Editable,
  at: Point2,
  playfield: PlayfieldSize,
  scale: number,
  strings: BenchStrings = BENCH_EN,
): InspectorView | undefined {
  const rows = inspectorRowsOf(device, strings)
  if (rows.length === 0) return undefined
  const width = CARD_WIDTH
  const height = cardHeight(rows.length)
  const s = scale > 0 ? scale : 1
  const halfW = width / 2 / s
  const halfH = height / 2 / s
  const x = (at[0] <= 0 ? -1 : 1) * (playfield.width / 2 + CARD_BESIDE / s + halfW)
  const y =
    halfH < playfield.length / 2
      ? Math.min(Math.max(at[1], -playfield.length / 2 + halfH), playfield.length / 2 - halfH)
      : at[1]
  // The words are part of the key: a card repainted for a language change is a card in the new one.
  const key = `${device.kind === 'wire' ? 'wire:' : ''}${device.id}|${rows.map((row) => `${row.path}=${row.value ?? '-'}=${row.text}=${row.label}`).join('|')}`
  return { deviceId: device.id, kind: device.kind, title: titleOf(device), rows, at: [x, y], width, height, key }
}

/**
 * The title's strip over the card's top edge, room metres (the 1.0.113 round): eight-tenths of the
 * card's width, 20 mm tall — seven-millimetre letters at a forearm's distance — and 3 mm clear of
 * the card, so it is never read as the first row. Not part of the card's hit test: it says what the
 * card is about and takes no gesture.
 */
export const TITLE_WIDTH = CARD_WIDTH * 0.8
export const TITLE_HEIGHT = 0.02
export const TITLE_GAP = 0.003

/** The title strip's centre in the card's frame: above the top edge. */
export function titleAt(view: InspectorView): number {
  return view.height / 2 + TITLE_GAP + TITLE_HEIGHT / 2
}

/** The card's title: the part's name and its id (the 1.0.113 round). */
export function titleOf(device: Editable): string {
  const handedness = (device as { readonly handedness?: unknown }).handedness
  const hand = handedness === 'left' || handedness === 'right' ? handedness : undefined
  return `${partLabel(device.kind as Parameters<typeof partLabel>[0], hand)} · ${device.id}`
}

/**
 * Where a wire's card is beside (26 Sep 2026): the middle of its control points' footprint, in
 * the playfield plane. The card goes to the side of it with more playfield, as a part's does.
 */
export function wireCardAt(spline: readonly Point3[]): Point2 {
  if (spline.length === 0) return [0, 0]
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const [x, y] of spline) {
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  }
  return [(minX + maxX) / 2, (minY + maxY) / 2]
}

/** Where row `index` of a card is. */
export function rowGeometry(view: InspectorView, index: number): RowGeometry {
  const row = view.rows[index]
  const y = view.height / 2 - CARD_PADDING - ROW_HEIGHT * (index + 0.5)
  // The text line takes the top of the row; the rail runs a little below the row's centre.
  const railY = y - ROW_HEIGHT * 0.2
  const minusX = -view.width / 2 + CARD_PADDING + TARGET / 2
  const plusX = view.width / 2 - CARD_PADDING - TARGET / 2
  const railLeft = minusX + TARGET / 2 + RAIL_GAP + TARGET / 2
  const railRight = plusX - TARGET / 2 - RAIL_GAP - TARGET / 2
  let knobX: number | undefined
  let knobY: number | undefined
  const dialX = row?.dial === true ? railLeft + DIAL_ALONG * (railRight - railLeft) : undefined
  if (row !== undefined && row.kind === 'rail' && row.value !== undefined && row.max > row.min) {
    if (dialX !== undefined) {
      const radians = (row.value * Math.PI) / 180
      knobX = dialX + DIAL_RADIUS * Math.cos(radians)
      knobY = railY + DIAL_RADIUS * Math.sin(radians)
    } else {
      const t = (row.value - row.min) / (row.max - row.min)
      knobX = railLeft + Math.min(1, Math.max(0, t)) * (railRight - railLeft)
      knobY = railY
    }
  }
  const labelLeft = minusX + TARGET / 2 + RAIL_GAP / 2
  const labelRight = dialX !== undefined ? dialX - DIAL_RADIUS - RAIL_GAP / 2 : railLeft + LABEL_SHARE * (railRight - railLeft)
  return { y, railLeft, railRight, railY, knobX, knobY, dialX, labelLeft, labelRight, minusX, plusX }
}

/** What a hand did to the card this frame. */
export type InspectorAction =
  /** A stepper tapped: the value moves one step. */
  | { readonly kind: 'step'; readonly path: string; readonly direction: -1 | 1 }
  /** A rail pinched: a slide begins — the next entry in the history. */
  | { readonly kind: 'grip'; readonly path: string }
  /** The pinch moved along the rail: the value it points at, in shown units. */
  | { readonly kind: 'slide'; readonly path: string; readonly value: number }
  /** The pinch opened: the slide is over. */
  | { readonly kind: 'release'; readonly path: string }

export interface InspectorFrame {
  readonly actions: readonly InspectorAction[]
  /** Hands over the card this frame — masked from the carry, so a rail is never a handle. */
  readonly over: { readonly left: boolean; readonly right: boolean }
  /** The hand sliding a rail, or nothing. */
  readonly holding: 'left' | 'right' | undefined
}

const NO_FRAME: InspectorFrame = { actions: [], over: { left: false, right: false }, holding: undefined }

interface Local {
  readonly x: number
  readonly y: number
  readonly z: number
}

/** A point in the playfield's frame, in the card's frame in room metres — the lean undone. */
function toCard(view: InspectorView, hand: CarryHand, scale: number): Local {
  const dy = (hand.y - view.at[1]) * scale
  const dz = hand.z * scale - cardLift(view)
  const c = Math.cos(CARD_TILT)
  const n = Math.sin(CARD_TILT)
  return { x: (hand.x - view.at[0]) * scale, y: dy * c + dz * n, z: -dy * n + dz * c }
}

function onCard(view: InspectorView, local: Local): boolean {
  return (
    Math.abs(local.x) <= view.width / 2 + CARD_SLACK &&
    Math.abs(local.y) <= view.height / 2 + CARD_SLACK &&
    local.z <= CARD_ABOVE &&
    local.z >= -CARD_BELOW
  )
}

const STEPPER = { width: TARGET, height: TARGET }

/**
 * How far in front of the card a dragging fingertip may come back before the drag lets go, and how
 * far off its row it may wander, metres (the 1.0.112 round). The drag begins on a tap through the
 * rail and follows the fingertip along it; it ends when the finger comes back out of the card — the
 * tap's own "out" distance — or strays a row and a half away.
 */
export const DRAG_OUT = TOUCH_OUT
export const DRAG_OFF_ROW = ROW_HEIGHT * 1.5

/** The face a fingertip drags a rail from: the rail and half a target past each end, a target and slack tall; a dial's disc. */
function railFace(view: InspectorView, index: number): { readonly width: number; readonly height: number; readonly x: number; readonly y: number } {
  const g = rowGeometry(view, index)
  if (g.dialX !== undefined) {
    const across = 2 * DIAL_RADIUS + TARGET
    return { width: across, height: across, x: g.dialX, y: g.railY }
  }
  return {
    width: g.railRight - g.railLeft + TARGET / 2,
    height: TARGET + CARD_SLACK,
    x: (g.railLeft + g.railRight) / 2,
    y: g.railY,
  }
}

/** Is a dragging fingertip still on the row it took, near enough the card to be dragging? */
function stillDragging(view: InspectorView, index: number, local: Local): boolean {
  const g = rowGeometry(view, index)
  return (
    local.z <= DRAG_OUT &&
    local.z >= -CARD_BELOW - DRAG_OUT &&
    Math.abs(local.y - g.railY) <= DRAG_OFF_ROW &&
    Math.abs(local.x) <= view.width / 2 + TARGET * 2
  )
}

export class InspectorInput {
  private readonly gates = new Map<string, Record<'left' | 'right', TapGate>>()
  private readonly wasGrab = { left: true, right: true }
  private sliding:
    | {
        readonly hand: 'left' | 'right'
        /** Pinched on the rail, or touched and dragged (the 1.0.112 round). */
        readonly by: 'pinch' | 'touch'
        readonly path: string
        readonly index: number
        last: number | undefined
      }
    | undefined
  private cardOf: string | undefined

  /** The hand sliding a rail, or nothing. */
  get holding(): 'left' | 'right' | undefined {
    return this.sliding?.hand
  }

  /** The row whose rail is being slid, or nothing — the picture lights its knob (the card polish, 26 Sep 2026). */
  get heldPath(): string | undefined {
    return this.sliding?.path
  }

  /**
   * One frame. `tips` are fingertips (the taps), `pinches` are pinch points (the slides), both
   * in the playfield's frame; `scale` is the bench's; `carrying` is true while a part is in a
   * hand, and then the card takes no gesture — a hand carrying a part over the card is carrying.
   */
  advance(
    view: InspectorView | undefined,
    tips: readonly CarryHand[],
    pinches: readonly CarryHand[],
    scale: number,
    carrying: boolean,
  ): InspectorFrame {
    if (view === undefined || carrying) {
      this.release()
      // The latches stay closed until every hand has been seen open, as the carry's do.
      for (const hand of pinches) this.wasGrab[hand.handedness] = hand.present ? hand.grab : true
      return NO_FRAME
    }
    // A different part: everything about the last card is forgotten.
    const card = `${view.kind === 'wire' ? 'wire:' : ''}${view.deviceId}`
    if (this.cardOf !== card) {
      this.release()
      this.cardOf = card
    }
    const actions: InspectorAction[] = []
    const over = { left: false, right: false }
    const s = scale > 0 ? scale : 1

    // --- the steppers, tapped -----------------------------------------------------------------
    view.rows.forEach((row, index) => {
      const geometry = rowGeometry(view, index)
      for (const [side, x, direction] of [
        ['minus', geometry.minusX, -1],
        ['plus', geometry.plusX, 1],
      ] as const) {
        const key = `${row.path}:${side}`
        let gates = this.gates.get(key)
        if (gates === undefined) {
          gates = { left: new TapGate(STEPPER), right: new TapGate(STEPPER) }
          this.gates.set(key, gates)
        }
        for (const hand of tips) {
          const gate = gates[hand.handedness]
          let state: TapState
          // A finger dragging a rail presses no stepper it slides into at the rail's end.
          const dragging = this.sliding?.by === 'touch' && this.sliding.hand === hand.handedness
          if (!hand.present || hand.grab || hand.atPlinth !== undefined || dragging) {
            state = gate.release()
          } else {
            const local = toCard(view, hand, s)
            if (onCard(view, local)) over[hand.handedness] = true
            state = gate.advance({ x: local.x - x, y: local.y - geometry.y, z: local.z })
          }
          if (state.pressedEdge) actions.push({ kind: 'step', path: row.path, direction })
        }
      }
    })

    // --- the rails, touched and dragged (the 1.0.112 round) --------------------------------------
    for (const hand of tips) {
      const sliding = this.sliding
      if (sliding !== undefined && sliding.by === 'touch' && sliding.hand === hand.handedness) {
        // The dragging hand is the card's wherever it has wandered along the row.
        over[hand.handedness] = true
        const row = view.rows[sliding.index]
        const local = hand.present ? toCard(view, hand, s) : undefined
        if (local === undefined || row === undefined || row.path !== sliding.path || !stillDragging(view, sliding.index, local)) {
          actions.push({ kind: 'release', path: sliding.path })
          this.sliding = undefined
          continue
        }
        const value = this.valueAt(view, sliding.index, local)
        if (value !== undefined && value !== sliding.last) {
          sliding.last = value
          actions.push({ kind: 'slide', path: row.path, value })
        }
        continue
      }
      view.rows.forEach((row, index) => {
        if (row.kind !== 'rail') return
        const face = railFace(view, index)
        const key = `${row.path}:rail`
        let gates = this.gates.get(key)
        if (gates === undefined) {
          gates = { left: new TapGate(face), right: new TapGate(face) }
          this.gates.set(key, gates)
        }
        const gate = gates[hand.handedness]
        let state: TapState
        let local: Local | undefined
        if (!hand.present || hand.grab || hand.atPlinth !== undefined) {
          state = gate.release()
        } else {
          local = toCard(view, hand, s)
          state = gate.advance({ x: local.x - face.x, y: local.y - face.y, z: local.z })
        }
        if (!state.pressedEdge || local === undefined || this.sliding !== undefined) return
        // A tap through the rail: the drag begins, and the value goes where the finger is.
        this.sliding = { hand: hand.handedness, by: 'touch', path: row.path, index, last: undefined }
        over[hand.handedness] = true
        actions.push({ kind: 'grip', path: row.path })
        const value = this.valueAt(view, index, local)
        if (value !== undefined && value !== row.value) {
          this.sliding.last = value
          actions.push({ kind: 'slide', path: row.path, value })
        }
      })
    }
    for (const key of [...this.gates.keys()]) {
      if (!view.rows.some((row) => key === `${row.path}:minus` || key === `${row.path}:plus` || key === `${row.path}:rail`)) {
        this.gates.delete(key)
      }
    }

    // --- the rails, pinched and slid ------------------------------------------------------------
    for (const hand of pinches) {
      const grab = hand.present && hand.grab
      const edge = grab && !this.wasGrab[hand.handedness]
      this.wasGrab[hand.handedness] = hand.present ? hand.grab : true
      if (!hand.present) continue
      const local = toCard(view, hand, s)
      const on = onCard(view, local)
      if (on) over[hand.handedness] = true

      const sliding = this.sliding
      if (sliding !== undefined && sliding.hand === hand.handedness && sliding.by === 'touch') {
        // Dragged by the fingertip: the pinch point is not the one that decides.
        over[hand.handedness] = true
        continue
      }
      if (sliding !== undefined && sliding.hand === hand.handedness) {
        // The sliding hand is the card's wherever it has wandered: a slide past the rail's end
        // is the end, not a pinch on the part under it.
        over[hand.handedness] = true
        if (!grab) {
          actions.push({ kind: 'release', path: sliding.path })
          this.sliding = undefined
          continue
        }
        const row = view.rows[sliding.index]
        if (row === undefined || row.path !== sliding.path) {
          actions.push({ kind: 'release', path: sliding.path })
          this.sliding = undefined
          continue
        }
        const value = this.valueAt(view, sliding.index, local)
        if (value !== undefined && value !== sliding.last) {
          sliding.last = value
          actions.push({ kind: 'slide', path: row.path, value })
        }
        continue
      }
      if (!edge || !on || hand.atPlinth !== undefined || this.sliding !== undefined) continue
      // A closing edge on a rail: the row whose rail band the pinch point is in.
      const index = view.rows.findIndex((row, i) => {
        if (row.kind !== 'rail') return false
        const g = rowGeometry(view, i)
        if (g.dialX !== undefined) {
          // A dial is taken anywhere on its face or within half a target of its rim.
          return (
            Math.hypot(local.x - g.dialX, local.y - g.railY) <= DIAL_RADIUS + TARGET / 2 &&
            local.z <= TOUCH_OUT &&
            local.z >= -CARD_BELOW
          )
        }
        return (
          Math.abs(local.y - g.railY) <= TARGET / 2 + CARD_SLACK / 2 &&
          local.x >= g.railLeft - TARGET / 2 &&
          local.x <= g.railRight + TARGET / 2 &&
          local.z <= TOUCH_OUT &&
          local.z >= -CARD_BELOW
        )
      })
      if (index < 0) continue
      const row = view.rows[index] as InspectorRow
      this.sliding = { hand: hand.handedness, by: 'pinch', path: row.path, index, last: undefined }
      actions.push({ kind: 'grip', path: row.path })
      const value = this.valueAt(view, index, local)
      if (value !== undefined && value !== row.value) {
        this.sliding.last = value
        actions.push({ kind: 'slide', path: row.path, value })
      }
    }

    return { actions, over, holding: this.sliding?.hand }
  }

  private valueAt(view: InspectorView, index: number, local: Local): number | undefined {
    const row = view.rows[index]
    if (row === undefined || row.max <= row.min) return undefined
    const g = rowGeometry(view, index)
    if (g.dialX !== undefined) {
      // A dial: the bearing the pinch points at from the centre, wherever the hand has wandered.
      const dx = local.x - g.dialX
      const dy = local.y - g.railY
      if (Math.hypot(dx, dy) < DIAL_DEAD) return undefined
      const degrees = round((Math.atan2(dy, dx) * 180) / Math.PI, row.step)
      return Math.min(row.max, Math.max(row.min, degrees))
    }
    const t = Math.min(1, Math.max(0, (local.x - g.railLeft) / (g.railRight - g.railLeft)))
    if (row.path === 'holdMs' && t <= 1e-9) return row.min
    return Math.min(row.max, Math.max(row.min, round(row.min + t * (row.max - row.min), row.step)))
  }

  /** Every gate let go and the slide dropped — the selection changed, the mode is leaving. */
  release(): void {
    for (const gates of this.gates.values()) {
      gates.left.release()
      gates.right.release()
    }
    this.gates.clear()
    this.sliding = undefined
    this.cardOf = undefined
  }
}
